import { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

import MarkdownMessage from "../../../../components/MarkdownMessage";
import { downloadAttachment } from "./api";
import { thinScrollbarSx } from "./constants";
import { ChatMessage } from "./types";

type MessageListProps = {
  messages: ChatMessage[];
};

const MessageList = ({ messages }: MessageListProps) => {
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const isStreaming = messages[messages.length - 1]?.pending === true;

    if (isStreaming) {
      // Durante o stream: coalesce num único frame por ciclo de render para
      // evitar animações concorrentes que causam glitch no overflow.
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "instant" as ScrollBehavior,
          block: "end",
        });
        rafRef.current = null;
      });
    } else {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [messages]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        setCopiedMessageId(null);
      }, 1500);
    } catch {
      // Sem feedback se o browser bloquear clipboard.
    }
  };

  return (
    <Stack
      spacing={1.5}
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        pr: 0.5,
        ...thinScrollbarSx,
      }}
    >
      {messages.map((message) => {
        const isAssistant = message.role === "assistant";
        const canCopy = isAssistant && !!message.content && !message.pending;

        return (
          <Stack
            key={message.id}
            direction="row"
            justifyContent={message.role === "user" ? "flex-end" : "flex-start"}
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                position: "relative",
                maxWidth: "85%",
                minWidth: 0,
                borderRadius: 2,
                px: 1.5,
                py: 1,
                pr: canCopy ? 4.5 : 1.5,
                bgcolor:
                  message.role === "user"
                    ? alpha(theme.palette.primary.main, 0.12)
                    : theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.06)
                    : alpha(theme.palette.common.black, 0.06),
              }}
            >
              {canCopy && (
                <Tooltip
                  title={copiedMessageId === message.id ? "Copiado!" : "Copiar"}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(message.id, message.content)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      color: "text.secondary",
                    }}
                  >
                    {copiedMessageId === message.id ? (
                      <CheckIcon sx={{ fontSize: 16 }} color="primary" />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {message.attachments.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  sx={{ mb: message.content ? 1 : 0 }}
                >
                  {message.attachments.map((attachment, index) => {
                    const chip = (
                      <Chip
                        icon={<InsertDriveFileOutlinedIcon />}
                        label={attachment.originalName}
                        deleteIcon={
                          attachment.storageKey ? (
                            <DownloadOutlinedIcon />
                          ) : undefined
                        }
                        onDelete={
                          attachment.storageKey
                            ? () => downloadAttachment(attachment.storageKey)
                            : undefined
                        }
                        sx={{
                          height: "auto",
                          py: 0.75,
                          px: 0.5,
                          "& .MuiChip-label": {
                            py: 0.25,
                            px: 1,
                          },
                          "& .MuiChip-icon": {
                            ml: 0.75,
                          },
                          "& .MuiChip-deleteIcon": {
                            mr: 0.5,
                            ml: 0.25,
                            fontSize: 20,
                            color: "primary.main",
                            opacity: 0.9,
                            borderRadius: "50%",
                            p: 0.25,
                            boxSizing: "content-box",
                            transition: "opacity 0.15s, background-color 0.15s",
                            "&:hover": {
                              opacity: 1,
                              color: "primary.main",
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.12),
                            },
                          },
                        }}
                      />
                    );

                    return (
                      <Box
                        key={`${
                          attachment.storageKey || attachment.originalName
                        }-${index}`}
                      >
                        {attachment.storageKey ? (
                          <Tooltip title="Baixar">{chip}</Tooltip>
                        ) : (
                          chip
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}

              {message.content && (
                <MarkdownMessage
                  content={message.content}
                  fontSize={message.role === "user" ? "0.875rem" : undefined}
                />
              )}

              {message.pending && !message.content && !message.statusLabel && (
                <CircularProgress size={16} thickness={5} />
              )}

              {message.pending && message.statusLabel && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ mt: message.content ? 1 : 0 }}
                >
                  <CircularProgress size={12} thickness={5} />
                  <Typography variant="caption" color="text.secondary">
                    {message.statusLabel}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        );
      })}
      <div ref={messagesEndRef} />
    </Stack>
  );
};

export default MessageList;
